"use client";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  IconBrandFacebookFilled,
  IconBrandLinkedinFilled,
  IconBrandMedium,
  IconBrandPinterestFilled,
  IconBrandReddit,
  IconBrandTelegram,
  IconBrandWhatsapp,
  IconBrandXFilled,
  IconMailFilled,
  IconPrinter,
  IconShare2,
} from "@tabler/icons-react";
import { Copy } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const shareLinks = [
  {
    label: "Facebook",
    icon: IconBrandFacebookFilled,
    color: "#4267B2",
    onClick: (url: string, title: string) => {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          url
        )}&t=${encodeURIComponent(title)}`,
        "_blank"
      );
    },
  },
  {
    label: "X",
    icon: IconBrandXFilled,
    color: "#000",
    onClick: (url: string, title: string) => {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          title
        )}&url=${encodeURIComponent(url)}`,
        "_blank"
      );
    },
  },
  {
    label: "WhatsApp",
    icon: IconBrandWhatsapp,
    color: "#25D366",
    onClick: (url: string, title: string) => {
      window.open(
        `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
        "_blank"
      );
    },
  },
  {
    label: "LinkedIn",
    icon: IconBrandLinkedinFilled,
    color: "#0077B5",
    onClick: (url: string) => {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          url
        )}`,
        "_blank"
      );
    },
  },
  {
    label: "Reddit",
    icon: IconBrandReddit,
    color: "#FF5700",
    onClick: (url: string, title: string) => {
      window.open(
        `https://www.reddit.com/submit?url=${encodeURIComponent(
          url
        )}&title=${encodeURIComponent(title)}`,
        "_blank"
      );
    },
  },
  {
    label: "Medium",
    icon: IconBrandMedium,
    color: "#000",
    onClick: (url: string) => {
      window.open(
        `https://medium.com/m/global-identity?redirectUrl=${encodeURIComponent(
          url
        )}`,
        "_blank"
      );
    },
  },
  {
    label: "Pinterest",
    icon: IconBrandPinterestFilled,
    color: "#E60023",
    onClick: (url: string, title: string, featuredImage: string) => {
      window.open(
        `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
          url
        )}&description=${encodeURIComponent(title)}&media=${encodeURIComponent(
          featuredImage || ""
        )}`,
        "_blank"
      );
    },
  },
  {
    label: "Telegram",
    icon: IconBrandTelegram,
    color: "#0088cc",
    onClick: (url: string, title: string) => {
      window.open(
        `https://t.me/share/url?url=${encodeURIComponent(
          url
        )}&text=${encodeURIComponent(title)}`,
        "_blank"
      );
    },
  },
  {
    label: "Email",
    icon: IconMailFilled,
    color: "#333",
    onClick: (url: string, title: string) => {
      window.open(
        `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(
          url
        )}`,
        "_blank"
      );
    },
  },
  {
    label: "Print",
    icon: IconPrinter,
    color: "#6c757d",
    onClick: () => {
      window.print();
    },
  },
];

export function HorizontalShare({
  title,
  description,
  url,
  featuredImage,
}: {
  title: string;
  description?: string;
  url?: string;
  featuredImage?: string;
}) {
  const [shareUrl, setShareUrl] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const { theme } = useTheme();

  const baseUrl =
    process.env.NEXT_PUBLIC_FRONTEND_URL || "https://moneyoval.com";

  const imageUrl = process.env.NEXT_PUBLIC_MEDIA_BASE_URL;

  // useEffect(() => {
  //   setShareUrl(baseUrl + (url || window.location.href));
  // }, [url, baseUrl]);

  useEffect(() => {
    const currentUrl = url ? baseUrl + url : window.location.href;
    setShareUrl(
      currentUrl.startsWith("http") ? currentUrl : baseUrl + currentUrl
    );
    console.log("Share URL:", currentUrl);
    console.log("Featured Image:", featuredImage);
  }, [url, baseUrl, featuredImage]);

  const handleShareClick = (
    shareAction: (url: string, title: string, featuredImage: string) => void
  ) => {
    shareAction(shareUrl, title, featuredImage!);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!");
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  return (
    <div className="w-full px-2 sm:px-4" id="mo-share-button-hr">
      <div className="flex flex-col sm:flex-row items-center justify-between py-0 gap-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <IconShare2 size={20} className="text-muted-foreground sm:w-auto" />
          <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
            {title ? title : "Share this article"}
          </span>
        </div>

        <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
          {shareLinks.map(({ label, icon: Icon, color, onClick }, index) => (
            <Tooltip key={label}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleShareClick(onClick)}
                  aria-label={`Share on ${label}`}
                >
                  <Icon
                    size={20}
                    className="sm:scale-125"
                    color={
                      theme === "dark" &&
                      (index === 1 || index === 5 || index === 8 || index === 9)
                        ? "#fff"
                        : color
                    }
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{label}</p>
              </TooltipContent>
            </Tooltip>
          ))}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 sm:h-10 sm:w-10 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                onClick={copyToClipboard}
                aria-label="Copy link"
              >
                <Copy
                  size={20}
                  className={`sm:scale-125 dark:text-white ${
                    isCopied ? "text-green-500" : "text-muted-foreground"
                  }`}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>{isCopied ? "Copied!" : "Copy link"}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
